const MILESTONES = [
  { tier: 'quick-trio', title: 'QUICK TRIO', count: 3, windowMs: 10000 },
  { tier: 'grove-rush', title: 'GROVE RUSH', count: 5, windowMs: 15000 },
];

function validBoardId(boardId) {
  return typeof boardId === 'string' && boardId.trim().length > 0 && boardId.length <= 200;
}

/** Start optional Bubble presentation achievements for one explicit run/seed/
 * board identity. This state is private feedback, never a score transcript.
 * An empty/oversized identity throws; callers must change it for a fresh run. */
export function createClusterFlourish(boardId) {
  if (!validBoardId(boardId)) throw new RangeError('Invalid Bubble board identity.');
  return { boardId, lastMove: 0, lastActiveMs: 0, bursts: [], awarded: [] };
}

/** Consume one already-accepted clear, immutably, in active milliseconds.
 * The caller supplies the canonical move number, size, awarded points and combo;
 * this helper cannot grant points or infer wall-clock time. Paused/hidden time
 * must be excluded by the caller. Three/five consecutive groups of at least four
 * within inclusive 10/15-second windows earn finite presentation milestones.
 * Small groups break this sequence. Each milestone appears once per board;
 * simultaneous first achievements show the stronger one and retire both.
 * Duplicate/stale moves are no-ops. Invalid events or backward new-move time
 * throw without changing history; a changed board identity resets history. */
export function advanceClusterFlourish(previous, event) {
  if (
    !event ||
    !validBoardId(event.boardId) ||
    !Number.isSafeInteger(event.move) ||
    event.move < 1 ||
    !Number.isFinite(event.activeMs) ||
    event.activeMs < 0 ||
    !Number.isInteger(event.size) ||
    event.size < 2 ||
    event.size > 256 ||
    !Number.isSafeInteger(event.points) ||
    event.points <= 0 ||
    !Number.isInteger(event.combo) ||
    event.combo < (event.size >= 4 ? 1 : 0) ||
    event.combo > (event.size >= 4 ? 5 : 0)
  )
    throw new RangeError('Invalid accepted Bubble clear.');
  const current =
    previous.boardId === event.boardId ? previous : createClusterFlourish(event.boardId);
  if (event.move <= current.lastMove) return { state: current, achievement: null };
  if (event.activeMs < current.lastActiveMs)
    throw new RangeError('Bubble active time moved backward.');
  const bursts =
      event.size >= 4
        ? [
            ...current.bursts.filter((burst) => event.activeMs - burst.activeMs <= 15000).slice(-4),
            {
              activeMs: event.activeMs,
              points: event.points,
            },
          ]
        : [],
    awarded = [...current.awarded];
  let achievement = null;
  for (const milestone of MILESTONES) {
    if (awarded.includes(milestone.tier) || bursts.length < milestone.count) continue;
    const sequence = bursts.slice(-milestone.count),
      elapsedMs = event.activeMs - sequence[0].activeMs;
    if (elapsedMs > milestone.windowMs) continue;
    const earnedPoints = sequence.reduce((sum, burst) => sum + burst.points, 0);
    if (!Number.isSafeInteger(earnedPoints)) throw new RangeError('Invalid Bubble point total.');
    awarded.push(milestone.tier);
    achievement = { ...milestone, elapsedMs, earnedPoints, combo: event.combo };
  }
  return {
    state: {
      boardId: event.boardId,
      lastMove: event.move,
      lastActiveMs: event.activeMs,
      bursts,
      awarded,
    },
    achievement,
  };
}
